import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import {
  cloneCompetitionApi,
  deleteCompetitionApi,
  disableCompetitionApi,
  exportCompetitionRegistrationsCsv,
  exportCompetitionResultsCsv,
  getCompetitionsApi,
  publishCompetitionApi,
} from '@/lib/api/competitionsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { buildPublishConfirmMessage } from '@/lib/competition-messages';
import { getFileUrl } from '@/lib/file-url';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { COMP_STATUS_CONFIG } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
} from '@shared';
import { CompetitionStatus, StripeAccountStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  BarChart2,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Copy,
  Download,
  Eye,
  ImageIcon,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

const FRONT_URL = import.meta.env.VITE_URL_FRONT as string;

// Statuts non modifiables (terminé, annulé ou désactivé par un super-admin)
const READ_ONLY_STATUSES = new Set([
  CompetitionStatus.FINISHED,
  CompetitionStatus.CANCELLED,
  CompetitionStatus.DISABLED_BY_ADMIN,
]);

type TabStatus = CompetitionStatus | '';

const VALID_STATUSES = new Set<string>(Object.values(CompetitionStatus));

const TABS: { label: string; status: TabStatus }[] = [
  { label: 'Tous', status: '' },
  { label: 'Brouillons', status: CompetitionStatus.DRAFT },
  { label: 'Inscriptions ouvertes', status: CompetitionStatus.REGISTRATION_OPEN },
  { label: 'En cours', status: CompetitionStatus.IN_PROGRESS },
  { label: 'Terminés', status: CompetitionStatus.FINISHED },
  { label: 'Désactivés', status: CompetitionStatus.DISABLED_BY_ADMIN },
];

const PAGE_SIZE = 10;

// Statuts qui autorisent la saisie des résultats
const CAN_ENTER_RESULTS_STATUSES = new Set([
  CompetitionStatus.REGISTRATION_CLOSED,
  CompetitionStatus.IN_PROGRESS,
  CompetitionStatus.FINISHED,
]);

function CompetitionActions({
  c,
  editUrl,
  resultsUrl,
  onPublish,
  onClone,
  onDelete,
  onDeactivate,
  isPublishing,
  isCloning,
  isDeleting,
  isDeactivating,
}: {
  c: Competition;
  editUrl: string;
  resultsUrl: string;
  onPublish: () => void;
  onClone: () => void;
  onDelete: () => void;
  onDeactivate: () => void;
  isPublishing: boolean;
  isCloning: boolean;
  isDeleting: boolean;
  isDeactivating: boolean;
}) {
  const [downloading, setDownloading] = useState<string | null>(null);

  const canEdit = !READ_ONLY_STATUSES.has(c.status);
  const isDisabled = c.status === CompetitionStatus.DISABLED_BY_ADMIN;
  const competitionDatePassed = c.date ? new Date(c.date) <= new Date() : false;
  const canEnterResults = CAN_ENTER_RESULTS_STATUSES.has(c.status) && competitionDatePassed;
  const menuItemBase =
    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';

  async function handleExport(type: 'registrations' | 'results') {
    setDownloading(type);
    try {
      if (type === 'registrations') {
        await exportCompetitionRegistrationsCsv(c.id, c.alias);
      } else {
        await exportCompetitionResultsCsv(c.id, c.alias);
      }
    } catch {
      toast.error("Erreur lors de l'export CSV");
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className='relative flex justify-end'>
      <details
        data-competition-actions
        className='group'
        onToggle={event => {
          const current = event.currentTarget;
          if (!current.open) return;

          document.querySelectorAll<HTMLDetailsElement>('details[data-competition-actions]').forEach(details => {
            if (details !== current) details.removeAttribute('open');
          });
        }}
      >
        <summary className='flex cursor-pointer list-none items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 [&::-webkit-details-marker]:hidden'>
          Actions
          <ChevronDown className='h-4 w-4 transition group-open:rotate-180' />
        </summary>

        <div className='absolute right-0 z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl'>
          {canEdit && (
            <Link to={editUrl} className={menuItemBase}>
              <Pencil className='h-4 w-4 text-amber-500' />
              Modifier
            </Link>
          )}

          {!isDisabled && (
            <button type='button' onClick={onClone} disabled={isCloning} className={menuItemBase}>
              <Copy className='h-4 w-4 text-indigo-500' />
              {isCloning ? 'Clonage…' : 'Cloner en brouillon'}
            </button>
          )}

          {isDisabled ? (
            <div className='flex items-center gap-2 px-3 py-2 text-sm text-slate-400'>
              <AlertTriangle className='h-4 w-4 text-red-400' />
              Concours désactivé par un administrateur
            </div>
          ) : c.status === CompetitionStatus.DRAFT ? (
            <>
              <button type='button' onClick={onPublish} disabled={isPublishing} className={menuItemBase}>
                <Send className='h-4 w-4 text-emerald-500' />
                {isPublishing ? 'Publication…' : 'Publier'}
              </button>

              <button
                type='button'
                onClick={onDelete}
                disabled={isDeleting}
                className={`${menuItemBase} text-red-600 hover:bg-red-50`}
              >
                <Trash2 className='h-4 w-4 text-red-500' />
                {isDeleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          ) : (
            <>
              <a href={`${FRONT_URL}/concours/${c.id}`} className={menuItemBase} target='_blank' rel='noreferrer'>
                <Eye className='h-4 w-4 text-blue-500' />
                Voir le concours
              </a>

              {canEnterResults && (
                <Link to={resultsUrl} className={menuItemBase}>
                  <ClipboardList className='h-4 w-4 text-violet-500' />
                  Saisir les résultats
                </Link>
              )}

              <button
                type='button'
                onClick={() => void handleExport('registrations')}
                disabled={downloading === 'registrations'}
                className={menuItemBase}
              >
                <Download className='h-4 w-4 text-emerald-500' />
                {downloading === 'registrations' ? 'Export…' : 'Exporter les inscriptions CSV'}
              </button>

              {c.status === CompetitionStatus.FINISHED && (
                <button
                  type='button'
                  onClick={() => void handleExport('results')}
                  disabled={downloading === 'results'}
                  className={menuItemBase}
                >
                  <BarChart2 className='h-4 w-4 text-purple-500' />
                  {downloading === 'results' ? 'Export…' : 'Exporter les résultats CSV'}
                </button>
              )}

              {/* Feature 8 (2026-08) — l'organisateur peut désactiver son concours
                  publié (le retire de la visibilité publique). Réactivation admin. */}
              {canEdit && (
                <button
                  type='button'
                  onClick={onDeactivate}
                  disabled={isDeactivating}
                  className={`${menuItemBase} text-red-600 hover:bg-red-50`}
                >
                  <Ban className='h-4 w-4 text-red-500' />
                  {isDeactivating ? 'Désactivation…' : 'Désactiver'}
                </button>
              )}
            </>
          )}
        </div>
      </details>
    </div>
  );
}

// ─── Desktop row (table) ─────────────────────────────────────────
function CompetitionRow({
  competition: c,
  editUrl,
  resultsUrl,
  onPublish,
  onClone,
  onDelete,
  onDeactivate,
  isPublishing,
  isCloning,
  isDeleting,
  isDeactivating,
}: {
  competition: Competition;
  editUrl: string;
  resultsUrl: string;
  onPublish: () => void;
  onClone: () => void;
  onDelete: () => void;
  onDeactivate: () => void;
  isPublishing: boolean;
  isCloning: boolean;
  isDeleting: boolean;
  isDeactivating: boolean;
}) {
  const cfg = COMP_STATUS_CONFIG[c.status];
  const statusBadgeClass =
    c.status === CompetitionStatus.DRAFT
      ? 'border border-violet-200 bg-violet-50 text-violet-700'
      : `border border-transparent ${cfg.bg} ${cfg.text}`;
  const progress = c.maxTeams ? Math.round((c.currentTeams / c.maxTeams) * 100) : 0;
  const barColor = progress >= 95 ? 'bg-red-500' : progress >= 70 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div
      className={`grid grid-cols-12 items-center gap-4 border-b border-slate-50 px-6 py-4 transition hover:bg-slate-50 ${
        c.status === CompetitionStatus.DRAFT ? '' : ''
      }`}
    >
      {/* Name + cover */}
      <div className='col-span-4 flex items-center gap-3 min-w-0'>
        <Link to={editUrl} className='shrink-0'>
          {c.files?.[0] ? (
            <img src={getFileUrl(c.files[0])} alt={c.name} className='h-10 w-10 rounded-lg object-cover' />
          ) : (
            <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100'>
              <ImageIcon className='h-4 w-4 text-slate-300' />
            </div>
          )}
        </Link>

        <div className='min-w-0'>
          <p className='truncate font-semibold text-slate-900'>
            <Link to={editUrl} title={'Modifier le concour'}>
              {c.name}
            </Link>
          </p>
          <p className='mt-0.5 text-xs text-slate-500'>
            {c.type ? TYPE_LABELS[c.type] : 'Brouillon'} · {c.level ? LEVEL_LABELS[c.level] : '—'}
          </p>
        </div>
      </div>

      {/* Date */}
      <div className='col-span-2 text-sm text-slate-600'>{c.date ? formatDateAbbreviatedFr(c.date) : '—'}</div>

      {/* Status */}
      <div className='col-span-2'>
        <span className={`inline-block rounded-full px-2 py-1 text-xs font-semibold ${statusBadgeClass}`}>
          {cfg.label}
        </span>
      </div>

      {/* Progress */}
      <div className='col-span-2'>
        <div className='flex items-center gap-2'>
          <div className='h-2 max-w-20 flex-1 rounded-full bg-slate-200'>
            <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${progress}%` }} />
          </div>
          <span className='text-xs text-slate-600'>
            {c.currentTeams}/{c.maxTeams ?? '?'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className='col-span-2'>
        <CompetitionActions
          c={c}
          editUrl={editUrl}
          resultsUrl={resultsUrl}
          onPublish={onPublish}
          onClone={onClone}
          onDelete={onDelete}
          onDeactivate={onDeactivate}
          isPublishing={isPublishing}
          isCloning={isCloning}
          isDeleting={isDeleting}
          isDeactivating={isDeactivating}
        />
      </div>
    </div>
  );
}

// ─── Mobile card row ─────────────────────────────────────────────
function CompetitionCard({
  competition: c,
  editUrl,
  resultsUrl,
  onPublish,
  onClone,
  onDelete,
  onDeactivate,
  isPublishing,
  isCloning,
  isDeleting,
  isDeactivating,
}: {
  competition: Competition;
  editUrl: string;
  resultsUrl: string;
  onPublish: () => void;
  onClone: () => void;
  onDelete: () => void;
  onDeactivate: () => void;
  isPublishing: boolean;
  isCloning: boolean;
  isDeleting: boolean;
  isDeactivating: boolean;
}) {
  const cfg = COMP_STATUS_CONFIG[c.status];
  const statusBadgeClass =
    c.status === CompetitionStatus.DRAFT
      ? 'border border-violet-200 bg-violet-50 text-violet-700'
      : `border border-transparent ${cfg.bg} ${cfg.text}`;
  const progress = c.maxTeams ? Math.round((c.currentTeams / c.maxTeams) * 100) : 0;
  const barColor = progress >= 95 ? 'bg-red-500' : progress >= 70 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-4 ${c.status === CompetitionStatus.DRAFT ? 'opacity-85' : ''}`}
    >
      <div className='flex items-start gap-3'>
        <Link to={editUrl} className='shrink-0'>
          {c.files?.[0] ? (
            <img src={getFileUrl(c.files[0])} alt={c.name} className='h-12 w-12 rounded-xl object-cover' />
          ) : (
            <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100'>
              <ImageIcon className='h-5 w-5 text-slate-300' />
            </div>
          )}
        </Link>

        <div className='min-w-0 flex-1'>
          <p className='font-bold text-slate-900 truncate'>{c.name}</p>
          <p className='mt-0.5 text-xs text-slate-500 truncate'>
            {c.type ? TYPE_LABELS[c.type] : 'Brouillon'} · {c.level ? LEVEL_LABELS[c.level] : '—'}
          </p>

          <div className='mt-2 flex flex-wrap items-center gap-2'>
            <span className='inline-flex items-center gap-1 text-xs text-slate-600'>
              <Calendar className='h-3.5 w-3.5' />
              {c.date ? formatDateAbbreviatedFr(c.date) : '—'}
            </span>

            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClass}`}
            >
              {cfg.label}
            </span>
          </div>
        </div>
      </div>

      <div className='mt-3'>
        <div className='flex items-center justify-between text-xs text-slate-600 mb-1'>
          <span>Inscriptions</span>
          <span>
            {c.currentTeams}/{c.maxTeams ?? '?'}
          </span>
        </div>
        <div className='h-2 w-full rounded-full bg-slate-200'>
          <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className='mt-3 flex justify-end'>
        <CompetitionActions
          c={c}
          editUrl={editUrl}
          resultsUrl={resultsUrl}
          onPublish={onPublish}
          onClone={onClone}
          onDelete={onDelete}
          onDeactivate={onDeactivate}
          isPublishing={isPublishing}
          isCloning={isCloning}
          isDeleting={isDeleting}
          isDeactivating={isDeactivating}
        />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────
export default function CompetitionList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL params (source of truth for filters)
  const rawStatus = searchParams.get('status') ?? '';
  const activeStatus: TabStatus = VALID_STATUSES.has(rawStatus) ? (rawStatus as CompetitionStatus) : '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const search = searchParams.get('search') ?? '';

  // Local input state for debounce
  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const queryClient = useQueryClient();
  const orgId = useAuthStore(s => s.user)?.organizationsAdmin[0]?.organizationId;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const { data: orgData } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId ?? 0),
    queryFn: () => getOrganizationApi(orgId!),
    enabled: !!orgId && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    ...(search && { search }),
    ...(activeStatus && { status: activeStatus }),
    ...(orgId && { organizationId: orgId }),
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.competitions.list(queryParams),
    queryFn: () => getCompetitionsApi(queryParams),
    placeholderData: keepPreviousData,
    // Gotcha Zustand : sans cette garde, la query part parfois AVANT la
    // réhydratation du token → requête sans Authorization → le serveur répond
    // en publicOnly et masque les concours DISABLED_BY_ADMIN (et résultat mis
    // en cache via keepPreviousData). On attend l'auth + l'orgId.
    enabled: !!orgId && isAuthenticated,
  });

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value || undefined, page: undefined });
    }, 350);
  }

  function handleTabChange(status: TabStatus) {
    updateParams({ status: status || undefined, page: undefined });
  }

  function handlePageChange(p: number) {
    updateParams({ page: p === 1 ? undefined : String(p) });
  }

  const publishMutation = useMutation({
    mutationFn: (id: number) => publishCompetitionApi(id),
    onSuccess: () => {
      toast.success('Concours publié — inscriptions ouvertes');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
    },
    onError: (err: unknown) => {
      const anyErr = err as {
        body?: { missingFields?: string[]; message?: string };
        response?: { data?: { missingFields?: string[]; message?: string } };
        message?: string;
      };
      const data = anyErr?.body ?? anyErr?.response?.data;
      if (data?.missingFields?.length) {
        toast.error(`Champs requis manquants : ${data.missingFields.join(', ')}`);
      } else {
        toast.error(data?.message ?? anyErr?.message ?? 'Erreur lors de la publication');
      }
    },
  });

  const cloneMutation = useMutation({
    mutationFn: (id: number) => cloneCompetitionApi(id),
    onSuccess: cloned => {
      toast.success(`Concours cloné en brouillon : ${cloned.name}`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
    },
    onError: () => {
      toast.error('Erreur lors du clonage du concours');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCompetitionApi(id),
    onSuccess: () => {
      toast.success('Concours supprimé');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
    },
  });

  // Feature 8 (2026-08) — désactivation par l'organisateur (réutilise l'endpoint
  // admin ; réactivation réservée au super-admin, cf. disclaimer de la modale).
  const [compToDeactivate, setCompToDeactivate] = useState<Competition | null>(null);

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => disableCompetitionApi(id),
    onSuccess: () => {
      toast.success('Concours désactivé — retiré de la visibilité publique');
      setCompToDeactivate(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la désactivation';
      toast.error(msg);
    },
  });

  const competitions = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  // Tant que l'auth/orgId ne sont pas prêts, la query est désactivée
  // (isLoading reste false) : on force l'état chargement pour éviter un flash
  // "Aucun concours" avant la première requête authentifiée.
  const isPageLoading = isLoading || !isAuthenticated || !orgId;
  const stripeSettingsUrl = `${ROUTES.ORGANIZER_SETTINGS}?tab=stripe`;
  const stripeNotActive = !!orgData && orgData.stripeAccountStatus !== StripeAccountStatus.ACTIVE;
  const showStripeBanner = stripeNotActive;
  const profileSettingsUrl = `${ROUTES.ORGANIZER_SETTINGS}?tab=preferences`;
  const showProfilePrivateBanner = !!orgData && orgData.isProfilePublic === false;

  const totalPages = data?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        <PageHeader
          title='Mes concours'
          subtitle='Gérez tous vos événements'
          actions={
            <Link
              to={ROUTES.COMPETITION_CREATE}
              className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700'
            >
              <Plus className='h-5 w-5' />
              Nouveau concours
            </Link>
          }
        />

        {/* Profil privé — concours invisibles publiquement */}
        {showProfilePrivateBanner && (
          <div className='mt-4 mb-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-red-500' />
            <p className='flex-1 text-sm text-red-800'>
              Votre profil public est désactivé. Vos concours ne sont pas visibles sur le site et n&apos;apparaissent
              pas dans les résultats de recherche.{' '}
              <Link to={profileSettingsUrl} className='font-semibold underline hover:text-red-900'>
                Activer le profil public
              </Link>
            </p>
          </div>
        )}

        {/* Stripe warning banner */}
        {showStripeBanner && (
          <div className='mt-4 mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' />
            <p className='flex-1 text-sm text-amber-800'>
              Votre compte Stripe n&apos;est pas encore actif. Les concours gratuits restent possibles, mais les
              concours payants sont bloques tant que Stripe Connect n&apos;est pas active.{' '}
              <Link to={stripeSettingsUrl} className='font-semibold underline hover:text-amber-900'>
                Ouvrir Paiement Stripe
              </Link>
            </p>
          </div>
        )}

        {/* Stat */}
        <div className='mb-6 sm:mb-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6'>
          <div className='flex items-center gap-4'>
            <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 shrink-0'>
              <Calendar className='h-6 w-6 text-blue-600' />
            </div>
            <div className='min-w-0'>
              <p className='text-3xl font-extrabold text-slate-900'>
                {isPageLoading ? '…' : total.toLocaleString('fr-FR')}
              </p>
              <p className='text-sm text-slate-500'>
                {activeStatus ? COMP_STATUS_CONFIG[activeStatus].label : 'Concours au total'}
              </p>
            </div>
            {isFetching && !isLoading && <span className='ml-auto text-xs text-slate-400'>Actualisation…</span>}
          </div>
        </div>

        {/* Search */}
        <div className='mb-4'>
          <div className='relative w-full max-w-md'>
            <Search className='absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder='Rechercher un concours...'
              className='w-full rounded-lg border border-slate-200 py-3 pl-10 pr-4 focus:border-blue-500 focus:outline-none'
            />
          </div>
        </div>

        {/* Table */}
        <div className='rounded-2xl border border-slate-200 bg-white overflow-visible'>
          {/* Tabs (scrollable on mobile) */}
          <div className='border-b border-slate-200'>
            <div className='flex overflow-x-auto [-webkit-overflow-scrolling:touch]'>
              {TABS.map(tab => (
                <button
                  key={tab.label}
                  onClick={() => handleTabChange(tab.status)}
                  className={`shrink-0 px-4 sm:px-6 py-4 text-sm font-semibold transition ${
                    activeStatus === tab.status
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                  {activeStatus === tab.status && total > 0 && (
                    <span className='ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700'>{total}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop header */}
          <div className='hidden md:grid grid-cols-12 gap-4 border-b border-slate-100 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500'>
            <div className='col-span-4'>Concours</div>
            <div className='col-span-2'>Date</div>
            <div className='col-span-2'>Statut</div>
            <div className='col-span-2'>Inscriptions</div>
            <div className='col-span-2 text-right'>Actions</div>
          </div>

          {/* Body */}
          {isPageLoading ? (
            <div>
              {/* Mobile skeleton */}
              <div className='md:hidden p-4 space-y-3'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-28 animate-pulse rounded-2xl bg-slate-100' />
                ))}
              </div>

              {/* Desktop skeleton */}
              <div className='hidden md:block'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='grid grid-cols-12 gap-4 border-b border-slate-50 px-6 py-4'>
                    <div className='col-span-4 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                  </div>
                ))}
              </div>
            </div>
          ) : isError ? (
            <div className='p-4 sm:p-6'>
              <QueryErrorState onRetry={() => void refetch()} />
            </div>
          ) : competitions.length === 0 ? (
            <div className='px-6 py-12 text-center text-sm text-slate-500'>Aucun concours trouvé</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className='md:hidden p-4 space-y-3'>
                {competitions.map(comp => (
                  <CompetitionCard
                    key={comp.id}
                    competition={comp}
                    editUrl={ROUTES.COMPETITION_EDIT.replace(':id', String(comp.id))}
                    resultsUrl={ROUTES.COMPETITION_RESULTS_ENTRY.replace(':id', String(comp.id))}
                    onPublish={() => {
                      if (!confirm(buildPublishConfirmMessage(comp.date))) return;
                      publishMutation.mutate(comp.id);
                    }}
                    onClone={() => {
                      if (!confirm(`Cloner « ${comp.name} » en brouillon ?`)) return;
                      cloneMutation.mutate(comp.id);
                    }}
                    onDelete={() => {
                      if (!confirm('Supprimer ce concours définitivement ?')) return;
                      deleteMutation.mutate(comp.id);
                    }}
                    onDeactivate={() => setCompToDeactivate(comp)}
                    isPublishing={publishMutation.isPending && publishMutation.variables === comp.id}
                    isCloning={cloneMutation.isPending && cloneMutation.variables === comp.id}
                    isDeleting={deleteMutation.isPending && deleteMutation.variables === comp.id}
                    isDeactivating={deactivateMutation.isPending && deactivateMutation.variables === comp.id}
                  />
                ))}
              </div>

              {/* Desktop table */}
              <div className='hidden md:block'>
                {competitions.map(comp => (
                  <CompetitionRow
                    key={comp.id}
                    competition={comp}
                    editUrl={ROUTES.COMPETITION_EDIT.replace(':id', String(comp.id))}
                    resultsUrl={ROUTES.COMPETITION_RESULTS_ENTRY.replace(':id', String(comp.id))}
                    onPublish={() => {
                      if (!confirm(buildPublishConfirmMessage(comp.date))) return;
                      publishMutation.mutate(comp.id);
                    }}
                    onClone={() => {
                      if (!confirm(`Cloner « ${comp.name} » en brouillon ?`)) return;
                      cloneMutation.mutate(comp.id);
                    }}
                    onDelete={() => {
                      if (!confirm('Supprimer ce concours définitivement ?')) return;
                      deleteMutation.mutate(comp.id);
                    }}
                    onDeactivate={() => setCompToDeactivate(comp)}
                    isPublishing={publishMutation.isPending && publishMutation.variables === comp.id}
                    isCloning={cloneMutation.isPending && cloneMutation.variables === comp.id}
                    isDeleting={deleteMutation.isPending && deleteMutation.variables === comp.id}
                    isDeactivating={deactivateMutation.isPending && deactivateMutation.variables === comp.id}
                  />
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-200 px-4 sm:px-6 py-4'>
            <p className='text-sm text-slate-500'>
              {total === 0
                ? 'Aucun résultat'
                : `Affichage de ${from} à ${to} sur ${total.toLocaleString('fr-FR')} concours`}
            </p>

            <div className='flex items-center gap-1 flex-wrap justify-start sm:justify-end'>
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                aria-label='Page précédente'
                className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
              >
                <ChevronLeft className='h-4 w-4' />
              </button>

              {getPageNumbers(page, totalPages).map((p, i) =>
                p === '...' ? (
                  <span key={`dots-${i}`} className='px-2 text-slate-400'>
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p as number)}
                    aria-current={p === page ? 'page' : undefined}
                    className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                      p === page ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}

              <button
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                aria-label='Page suivante'
                className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
              >
                <ChevronRight className='h-4 w-4' />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feature 8 (2026-08) — modale de confirmation désactivation organisateur */}
      {compToDeactivate && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div
            className='absolute inset-0 bg-black/50 backdrop-blur-sm'
            onClick={() => !deactivateMutation.isPending && setCompToDeactivate(null)}
          />
          <div className='relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'>
            <div className='mb-4 flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-red-100'>
                <Ban className='h-5 w-5 text-red-600' />
              </div>
              <h3 className='text-lg font-bold text-slate-900'>Désactiver ce concours ?</h3>
            </div>
            <p className='mb-2 text-sm text-slate-600'>
              <strong>« {compToDeactivate.name} »</strong> sera retiré de la visibilité publique (recherche, carte,
              compteurs) et les inscriptions seront bloquées.
            </p>
            <p className='mb-5 rounded-lg bg-amber-50 p-3 text-xs text-amber-700'>
              ⚠️ Attention : seul un administrateur BoulesConnect pourra le réactiver. Contactez le support si vous
              souhaitez le republier ultérieurement.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setCompToDeactivate(null)}
                disabled={deactivateMutation.isPending}
                className='rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50'
              >
                Annuler
              </button>
              <button
                type='button'
                onClick={() => deactivateMutation.mutate(compToDeactivate.id)}
                disabled={deactivateMutation.isPending}
                className='flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50'
              >
                <Ban className='h-4 w-4' />
                {deactivateMutation.isPending ? 'Désactivation…' : 'Désactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
